// Thai utility functions for Sharikah-53

/**
 * Thai currency formatting utilities
 */
export class ThaiCurrency {
  /**
   * Format number as Thai currency
   */
  static format(amount: number): string {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  /**
   * Format number as Thai currency without symbol
   */
  static formatNumber(amount: number): string {
    return new Intl.NumberFormat('th-TH', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  /**
   * Convert number to Thai text (for official documents)
   * Example: 1234 -> "หนึ่งพันสองร้อยสามสิบสี่บาท"
   */
  static toText(amount: number): string {
    const units = ['', 'สิบ', 'ร้อย', 'พัน', 'หมื่น', 'แสน', 'ล้าน']
    const numbers = ['', 'หนึ่ง', 'สอง', 'สาม', 'สี่', 'ห้า', 'หก', 'เจ็ด', 'แปด', 'เก้า']
    
    if (amount === 0) return 'ศูนย์บาท'
    
    const wholePart = Math.floor(amount)
    const fractionalPart = Math.round((amount - wholePart) * 100)
    
    let result = ''
    
    // Convert whole part
    if (wholePart > 0) {
      result += convertWholeNumber(wholePart)
      result += 'บาท'
    }
    
    // Convert fractional part (satang)
    if (fractionalPart > 0) {
      result += convertWholeNumber(fractionalPart)
      result += 'สตางค์'
    }
    
    return result || 'ศูนย์บาท'
    
    function convertWholeNumber(num: number): string {
      if (num === 0) return ''
      
      const str = num.toString()
      const len = str.length
      let text = ''
      
      for (let i = 0; i < len; i++) {
        const digit = parseInt(str[i])
        const position = len - i - 1
        
        if (digit === 0) continue
        
        // Special cases for Thai numbers
        if (position === 1 && digit === 2) {
          text += 'ยี่'
        } else if (position === 1 && digit === 1) {
          text += 'สิบ'
          continue
        } else if (position === 0 && digit === 1 && len > 1) {
          text += 'เอ็ด'
        } else {
          text += numbers[digit]
        }
        
        if (position > 0) {
          text += units[position]
        }
      }
      
      return text
    }
  }
}

/**
 * Thai phone number formatting utilities
 */
export class ThaiPhone {
  /**
   * Format Thai phone number
   * Example: "0812345678" -> "081-234-5678"
   */
  static format(phone: string): string {
    const cleaned = phone.replace(/\D/g, '')
    
    if (cleaned.length === 10 && cleaned.startsWith('0')) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
    }
    
    if (cleaned.length === 9) {
      return `0${cleaned.slice(0, 2)}-${cleaned.slice(2, 5)}-${cleaned.slice(5)}`
    }
    
    return phone // Return original if can't format
  }

  /**
   * Validate Thai phone number
   */
  static isValid(phone: string): boolean {
    const cleaned = phone.replace(/\D/g, '')
    
    // Thai mobile numbers start with 06x, 08x, 09x
    const mobileRegex = /^0[689]\d{8}$/
    
    return mobileRegex.test(cleaned)
  }

  /**
   * Clean phone number (remove formatting)
   */
  static clean(phone: string): string {
    return phone.replace(/\D/g, '')
  }
}

/**
 * Thai date formatting utilities including Buddhist calendar
 */
export class ThaiDate {
  /**
   * Convert Gregorian year to Buddhist year
   */
  static toBuddhistYear(gregorianYear: number): number {
    return gregorianYear + 543
  }

  /**
   * Convert Buddhist year to Gregorian year
   */
  static toGregorianYear(buddhistYear: number): number {
    return buddhistYear - 543
  }

  /**
   * Format date in Thai Buddhist calendar
   */
  static formatBuddhist(date: Date | string): string {
    const d = typeof date === 'string' ? new Date(date) : date
    
    return new Intl.DateTimeFormat('th-TH-u-ca-buddhist', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(d)
  }

  /**
   * Format date in short Thai format
   */
  static formatShort(date: Date | string): string {
    const d = typeof date === 'string' ? new Date(date) : date
    
    return new Intl.DateTimeFormat('th-TH', {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
    }).format(d)
  }

  /**
   * Format date in long Thai format
   */
  static formatLong(date: Date | string): string {
    const d = typeof date === 'string' ? new Date(date) : date
    
    return new Intl.DateTimeFormat('th-TH', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(d)
  }

  /**
   * Get Thai month names
   */
  static getMonthNames(): string[] {
    return [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ]
  }

  /**
   * Get Thai day names
   */
  static getDayNames(): string[] {
    return [
      'อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์'
    ]
  }

  /**
   * Get current date in Buddhist calendar format
   */
  static nowBuddhist(): string {
    return this.formatBuddhist(new Date())
  }

  /**
   * Parse Buddhist date string to Gregorian Date
   */
  static parseBuddhist(buddhistDateStr: string): Date | null {
    try {
      // Expected format: "DD/MM/YYYY" where YYYY is Buddhist year
      const parts = buddhistDateStr.split('/')
      if (parts.length !== 3) return null
      
      const day = parseInt(parts[0])
      const month = parseInt(parts[1])
      const buddhistYear = parseInt(parts[2])
      const gregorianYear = this.toGregorianYear(buddhistYear)
      
      return new Date(gregorianYear, month - 1, day)
    } catch {
      return null
    }
  }
}

/**
 * Thai number utilities
 */
export class ThaiNumber {
  /**
   * Convert Arabic numerals to Thai numerals
   * Example: "123" -> "๑๒๓"
   */
  static toThai(num: string | number): string {
    const thaiDigits = ['๐', '๑', '๒', '๓', '๔', '๕', '๖', '๗', '๘', '๙']
    
    return num.toString().replace(/\d/g, (digit) => thaiDigits[parseInt(digit)])
  }

  /**
   * Convert Thai numerals to Arabic numerals
   * Example: "๑๒๓" -> "123"
   */
  static toArabic(thaiNum: string): string {
    const arabicDigits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']
    const thaiDigits = ['๐', '๑', '๒', '๓', '๔', '๕', '๖', '๗', '๘', '๙']
    
    let result = thaiNum
    thaiDigits.forEach((thai, index) => {
      result = result.replace(new RegExp(thai, 'g'), arabicDigits[index])
    })
    
    return result
  }

  /**
   * Format number with Thai thousand separators
   */
  static formatWithSeparator(num: number): string {
    return new Intl.NumberFormat('th-TH').format(num)
  }
}

/**
 * Thai text utilities
 */
export class ThaiText {
  /**
   * Truncate Thai text properly (considering Thai characters)
   */
  static truncate(text: string, maxLength: number, suffix = '...'): string {
    if (text.length <= maxLength) return text
    
    return text.slice(0, maxLength - suffix.length) + suffix
  }

  /**
   * Count Thai characters properly
   */
  static length(text: string): number {
    // Use Intl.Segmenter for proper Thai character counting
    if (typeof (Intl as any).Segmenter !== 'undefined') {
      const segmenter = new (Intl as any).Segmenter('th', { granularity: 'grapheme' })
      return Array.from(segmenter.segment(text)).length
    }
    
    // Fallback for older browsers
    return text.length
  }

  /**
   * Check if text contains Thai characters
   */
  static containsThai(text: string): boolean {
    const thaiRegex = /[\u0E00-\u0E7F]/
    return thaiRegex.test(text)
  }

  /**
   * Normalize Thai text (remove tone marks for search)
   */
  static normalize(text: string): string {
    // Remove Thai tone marks and diacritics for better search
    return text.normalize('NFD').replace(/[\u0E34-\u0E3A\u0E47-\u0E4E]/g, '')
  }
}

/**
 * Validation utilities for Thai data
 */
export class ThaiValidation {
  /**
   * Validate Thai ID card number
   */
  static isValidIdCard(id: string): boolean {
    const cleaned = id.replace(/\D/g, '')
    
    if (cleaned.length !== 13) return false
    
    // Thai ID card checksum algorithm
    let sum = 0
    for (let i = 0; i < 12; i++) {
      sum += parseInt(cleaned[i]) * (13 - i)
    }
    
    const checkDigit = (11 - (sum % 11)) % 10
    return checkDigit === parseInt(cleaned[12])
  }

  /**
   * Validate Thai postal code
   */
  static isValidPostalCode(postalCode: string): boolean {
    const cleaned = postalCode.replace(/\D/g, '')
    return /^\d{5}$/.test(cleaned)
  }
}

/**
 * Comprehensive Thai utilities object
 */
export const Thai = {
  Currency: ThaiCurrency,
  Phone: ThaiPhone,
  Date: ThaiDate,
  Number: ThaiNumber,
  Text: ThaiText,
  Validation: ThaiValidation,
}

// Convenience exports
export const formatThaiPhoneNumber = ThaiPhone.format